import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatChipSelectionChange, MatChipsModule } from '@angular/material/chips';
import { MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { ComponentLoader } from '@app/components/shared/component-loader/component-loader';
import { SharedDialog } from '@app/components/shared/shared-dialog/shared-dialog';
import { CharLimitEnum } from '@app/constants/char-limit.enum';
import { RouteEnum } from '@app/constants/route.enum';
import { StringEnum } from '@app/constants/string.enum';
import { UtilityEnum } from '@app/constants/utility.enum';
import { PincodeServiceabilityReq } from '@app/models/global.model';
import { PopularCity } from '@app/models/utility.model';
import { Global } from '@app/services/global/global';
import { Loader } from '@app/services/loader/loader';
import { MyProfile } from '@app/services/my-profile/my-profile';
import { Utility } from '@app/services/utility/utility';
import { AppStore } from '@app/stores/app.store';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-location-change-dialog',
  imports: [
    SharedDialog,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    ComponentLoader,
  ],
  templateUrl: './location-change-dialog.html',
  styleUrl: './location-change-dialog.scss',
})
export class LocationChangeDialog implements OnInit {
  protected readonly stringEnum = StringEnum;
  protected readonly utilityEnum = UtilityEnum;
  protected readonly charLimitEnum = CharLimitEnum;
  protected readonly selectedCity = signal<string | null>(null);
  protected isLoggedIn = computed(() => this.appStore.isLoggedIn());
  protected pincodeTouched = signal<boolean>(false);
  protected pincode = signal<string>('');
  protected isPincodeValid = computed(() =>
    new RegExp(this.utilityEnum.PINCODE_REGEX).test(this.pincode()),
  );
  protected userAddress = signal<string>('');
  protected locationUnserviceable = signal<boolean>(false);
  protected popularCities = signal<PopularCity[]>([
    {
      name: 'Delhi',
      alternativeText: 'Delhi icon',
      iconUrl: '/icons/delhi-logo-big.svg',
      selected: false,
    },
    {
      name: 'Noida',
      alternativeText: 'Noida icon',
      iconUrl: '/icons/noida-logo-big.svg',
      selected: false,
    },
    {
      name: 'Gurugram',
      alternativeText: 'Gurugram icon',
      iconUrl: '/icons/gurugram-logo-big.svg',
      selected: false,
    },
    {
      name: 'Ghaziabad',
      alternativeText: 'Ghaziabad icon',
      iconUrl: '/icons/ghaziabad-logo-big.svg',
      selected: false,
    },
    {
      name: 'Faridabad',
      alternativeText: 'Faridabad icon',
      iconUrl: '/icons/faridabad-logo-big.svg',
      selected: false,
    },
  ]);
  private readonly dialogRef = inject(MatDialogRef<LocationChangeDialog>);
  private readonly utilityService = inject(Utility);
  private readonly appStore = inject(AppStore);
  private readonly myProfileService = inject(MyProfile);
  private readonly globalService = inject(Global);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly loaderService = inject(Loader);

  ngOnInit(): void {
    if (this.isLoggedIn()) {
      this.getUserAddress();
    }
  }

  protected onClose(): void {
    this.dialogRef.close();
  }

  protected async onAutoDetectLocation(): Promise<void> {
    this.loaderService.showComponentLoader();
    try {
      const pincode = await this.utilityService.getCurrentLocationPincodeGoogleJs();
      this.pincode.set(pincode || '');
    } finally {
      this.loaderService.hideComponentLoader();
      this.pincodeTouched.set(true);
    }
  }

  protected onPincodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digitsOnly = input.value.replaceAll(
      new RegExp(this.utilityEnum.NON_DIGITS_REGEX, 'g'),
      '',
    );
    input.value = digitsOnly;
    this.pincode.set(digitsOnly);
  }

  protected onPincodeBlur(): void {
    this.pincodeTouched.set(true);
  }

  protected async onCheckLocation(): Promise<void> {
    this.loaderService.showComponentLoader();
    const payload: PincodeServiceabilityReq = {
      pincode: this.pincode(),
    };
    this.globalService
      .checkServiceability(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loaderService.hideComponentLoader()),
      )
      .subscribe({
        next: (res) => {
          if (res?.status && res?.data?.isServiceable) {
            this.locationUnserviceable.set(false);
            this.selectedCity.set(res?.data?.serviceableHubs[0]?.hubName || '');
            this.utilityService.setLocalStorageItem<string>(
              UtilityEnum.CURRENT_CITY_STORAGE_KEY,
              this.selectedCity(),
            );
            this.utilityService.setLocalStorageItem<string>(
              UtilityEnum.CURRENT_PINCODE_STORAGE_KEY,
              this.pincode(),
            );
            this.appStore.setSelectedPincode(this.pincode());
            this.appStore.setSelectedCity(this.selectedCity());
            this.dialogRef.close();
          } else {
            this.locationUnserviceable.set(true);
          }
        },
        error: (err) => {
          this.utilityService.showErrorToaster(err);
        },
      });
  }

  protected onCitySelected(event: MatChipSelectionChange): void {
    this.selectedCity.set(event?.source?.value);
  }

  private getUserAddress(): void {
    this.loaderService.showComponentLoader();
    const contact = this.appStore.contactNo() || '';
    this.myProfileService
      .getDeliveryAddress(contact)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loaderService.hideComponentLoader()),
      )
      .subscribe({
        next: (res) => {
          if (res?.data?.address?.length > 0) {
            this.userAddress.set(
              this.utilityService.createAddressString(
                res.data.address[0]?.deliveryFloor,
                res.data.address[0]?.deliveryBuildingName,
                res.data.address[0]?.deliveryTowerWing,
                res.data.address[0]?.deliveryNearbyLandmark,
                res.data.address[0]?.pinCode,
                res.data.address[0]?.address1,
              ),
            );
          }
        },
        error: (err) => {
          this.utilityService.showErrorToaster(err);
        },
      });
  }

  protected onEditAddress(): void {
    this.router.navigate([RouteEnum.ADDRESS_BOOK]);
    this.dialogRef.close();
  }
}
